import React from 'react';
import { CircularProgress } from '@mui/material';
import { useAppSelector } from '../../../../hooks/redux';
import TableItem from '../TableItem';
import { EDirection } from '../../../../store/reducers/enumTypes';
import Status from '../Status';
import css from './Table.module.scss';
import { TPaymentData, TPaymentFXData } from '../../../../store/reducers/paymentSlice';
import { SafeHtmlParser } from '../../../../components/SafeHtmlParser';

// Typeguard
function isPaymentFX(
  data?: TPaymentData[] | TPaymentFXData[],
): data is TPaymentFXData[] {
  const dataEl = data?.length && data[0];
  if ((dataEl as TPaymentFXData)?.fxTrxId) {
    return true;
  }
  return false;
}

function getMappedData(data?: TPaymentData[] | TPaymentFXData[], direction?: EDirection) {
  if (isPaymentFX(data)) {
    if (direction === EDirection.FX) {
      return {
        header: [
          'Transaction ID',
          'Channel',
          'Client',
          'Exhange Rate',
          'Amount Buy',
          'Amount Sell',
          'Value Date',
          'Status',
        ],
        content: data?.map((payment) => [
          payment.refIdClient,
          payment.corrBankName || '',
          payment.name || '',
          payment.bankRate || '',
          <SafeHtmlParser as='div'>{`${payment.buyDetails.currency} ${payment.buyDetails.amountBank}`}</SafeHtmlParser>,
          <SafeHtmlParser as='div'>{`${payment.sellDetails.currency} ${payment.sellDetails.amountBank}`}</SafeHtmlParser>,
          payment.valueDate,
          <Status status={payment.status} />,
        ]),
        collapse: data?.map((payment) => [
          { key: 'Direction:', value: payment.direction },
          { key: 'Account from:', value: payment.dtAccountNumber },
          { key: 'Account to:', value: payment.ctAccountNumber },
          {
            key: 'Client Amount Buy:',
            value: payment.buyDetails ? `${payment.buyDetails.currency} ${payment.buyDetails.amountClient}` : '',
          },
          {
            key: 'Client Amount Sell:',
            value: payment.sellDetails ? `${payment.sellDetails.currency} ${payment.sellDetails.amountClient}` : '',
          },
          { key: 'Client Exchange Rate: ', value: `${payment.clientRate}` },
        ]),
      }
    }
  } else {
  switch (direction) {
    case EDirection.OUTWARD:
      return {
        header: [
          'Transaction ID',
          'Channel',
          'Client',
          'Beneficiary',
          'Beneficiary Bank',
          'Amount',
          'Value Date',
          'Status',
        ],
        content: data?.map((payment) => [
          payment.refIdClient,
          payment.corrBankName || '',
          payment.origDetails?.name || '',
          payment.benDetails?.name || '',
          <SafeHtmlParser as='div'>{`${payment.benDetails?.bankName ?? ''}<br />${payment.benDetails?.bankId ?? ''}`}</SafeHtmlParser>,
          `${payment.currency} ${payment.amount}`,
          payment.valueDate,
          <Status status={payment.status} />,
        ]),
        collapse: data?.map((payment) => [
          { key: 'Client Account:', value: payment.origDetails?.accountNumber || '' },
          { key: 'Beneficiary Account:', value: payment.benDetails?.accountNumber || '' },
          {
            key: 'Remittance  Information:',
            value: payment.paymentInfo?.remittanceInfo || '',
          },
          {
            key: 'Service Fee:',
            value: payment.fees ? `${payment.fees.clientFeeCurrency} ${payment.fees.clientFeeAmount}` : '',
          },
          { key: 'Correspondent Bank Fee:', value: payment.fees ? `${payment.fees.corrBankFeeCurrency} ${payment.fees.corrBankFeeAmount}` : ''},
          {
            key: 'Correspondent Bank Fee Type:',
            value: payment.fees ? payment.fees.paymentFeeType : '',
          },
        ]),
      };
    case EDirection.INWARD:
      return {
        header: [
          'Transaction ID',
          'Channel',
          'Client',
          'Originator',
          'Originator Bank',
          'Amount',
          'Value Date',
          'Status',
        ],
        content: data?.map((payment) => [
          payment.refIdBank,
          payment.corrBankName || '',
          payment.benDetails?.name || '',
          payment.origDetails?.name || '',
          <SafeHtmlParser as='div'>{`${payment.origDetails?.bankName ?? ''}<br />${payment.origDetails?.bankId ?? ''}`}</SafeHtmlParser>,
          `${payment.currency} ${payment.amount}`,
          payment.valueDate,
          <Status status={payment.status} />,
        ]),
        collapse: data?.map((payment) => [
          { key: 'Client Account:', value: payment.benDetails?.accountNumber || '' },
          { key: 'Originator Account:', value: payment.origDetails?.accountNumber || '' },
          {
            key: 'Remittance  Information:',
            value: payment.paymentInfo?.remittanceInfo || '',
          },
          {
            key: 'Service Fee:',
            value: payment.fees ? `${payment.fees.clientFeeCurrency} ${payment.fees.clientFeeAmount}` : '',
          },
          { key: 'Correspondent Bank Fee:', value: payment.fees ? `${payment.fees.corrBankFeeCurrency} ${payment.fees.corrBankFeeAmount}` : ''},
          {
            key: 'Correspondent Bank Fee Type:',
            value: payment.fees ? payment.fees.paymentFeeType : '',
          },
        ]),
      };
    default:
      return {
        header: [
          'Transaction ID',
          'Channel',
          'Debit Client',
          'Credit Client',
          'Amount',
          'Value Date',
          'Status',
        ],
        content: data?.map((payment) => [
          payment.refIdClient,
          payment.corrBankName || '',
          payment.origDetails?.name || '',
          payment.benDetails?.name || '',
          `${payment.currency} ${payment.amount}`,
          payment.valueDate,
          <Status status={payment.status} />,
        ]),
        collapse: data?.map((payment) => [
          { key: 'Debit Account:', value: payment.origDetails?.accountNumber || '' },
          { key: 'Credit Account:', value: payment.benDetails?.accountNumber || '' },
          {
            key: 'Remittance  Information:',
            value: payment.paymentInfo?.remittanceInfo || '',
          },
          {
            key: 'Service Fee:',
            value: payment.fees ? `${payment.fees.clientFeeCurrency} ${payment.fees.clientFeeAmount}` : '',
          },
          { key: 'Correspondent Bank Fee:', value: payment.fees ? `${payment.fees.corrBankFeeCurrency} ${payment.fees.corrBankFeeAmount}` : ''}
        ]),
      };
  }
  }
};

export const Table = () => {
  const { filters } = useAppSelector((state) => state.filtersReducer);
  const { payment, paymentFX, loading } = useAppSelector((state) => state.paymentsReducer);

  if (loading) {
    return <CircularProgress />;
  }
  
  const table = getMappedData(filters.direction === EDirection.FX ? paymentFX?.data : payment?.data, filters.direction);

  return (
    <div className={css.root}>
      <div className={css.header}>
        {table?.header.map((name, index) => <div className={css[`col${index + 1}`]} key={`header_${index}`}>{name}</div>)}
      </div>
      <div className={css.boby}>
        {table?.content?.map((item, index) => (
          <TableItem key={`tableItem_${index}`} item={item} collapse={table?.collapse ? table?.collapse[index] : []} />
        ))}
      </div>
    </div>
  );
};
